import React, { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { CartModalProps } from '../../types/cart';
import CartItem from './CartItem';
import { DepartmentOfficeSelector } from '../admin';
import { useOrderDepartment } from '../../hooks';

const CartModal: React.FC<CartModalProps> = ({
  isOpen,
  onClose,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onOrderComplete,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Custom hook for department/office management
  const { departments, offices, selection, loading, handleDepartmentChange, handleOfficeChange } =
    useOrderDepartment(isOpen);

  if (!isOpen) {
    return null;
  }

  const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleOrderComplete = async () => {
    if (!onOrderComplete) {
      return;
    }

    // Departman ve ofis seçilmiş mi kontrol et
    if (!selection.department || !selection.office) {
      setError('Lütfen departman ve ofis seçiniz');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      await onOrderComplete(total, {
        management: selection.department,
        office: selection.office,
      });
      onClose(); // Başarılı olursa modalı kapat
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sipariş tamamlanırken bir hata oluştu');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="absolute inset-0" onClick={onClose} aria-label="Kapat" />
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[81vh] overflow-hidden relative">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">Sepetim</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
            <FaTimes size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[54vh]">
          {cartItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Sepetinizde ürün bulunmamaktadır.</div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item, index) => (
                <CartItem
                  key={`${item.id}-${JSON.stringify(item.options)}-${index}`}
                  item={item}
                  index={index}
                  onUpdateQuantity={onUpdateQuantity}
                  onRemoveItem={onRemoveItem}
                />
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {cartItems.length > 0 && (
            <DepartmentOfficeSelector
              departments={departments}
              offices={offices}
              selection={selection}
              onDepartmentChange={handleDepartmentChange}
              onOfficeChange={handleOfficeChange}
              loading={loading}
            />
          )}
          <div className="flex justify-between items-center mb-4">
            <span className="text-lg font-semibold text-gray-800">Toplam</span>
            <span className="text-2xl font-bold text-green-600">{total} Fiş</span>
          </div>
          <button
            className="w-full bg-gradient-to-r from-green-500 to-green-400 text-white py-3 rounded-xl font-bold text-lg shadow hover:from-green-600 hover:to-green-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={cartItems.length === 0 || loading || isProcessing}
            onClick={handleOrderComplete}
          >
            {isProcessing ? 'Sipariş İşleniyor...' : loading ? 'Yükleniyor...' : 'Siparişi Tamamla'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartModal;
